import { useMemo } from "react";
import { ScrollView } from "react-native";
import { addWeeks, startOfWeek } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import { View, Card } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { useAlarm } from "@src/services/api/hooks";
import { WeeklySchedule, type ScheduleHistoryEntry } from "@src/features/schedules/components/WeeklySchedule";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { useAlarmScheduleHistory } from "@visioni/a3s-alarm-schedules/app";
import { DataStateView } from "@src/components/screen-state";
import { AlarmConfigSections } from "@src/features/alarms/components/AlarmConfigSections";
import { deriveAlarmSettingsViewState } from "@src/features/alarms/utils/alarm-settings-view-state";
import { useRouter } from "expo-router";
import { useTenantId } from "@src/features/tenants";
import { useCameraPermissions } from "@visioni/a3s-access/app";
import { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import { toISODateString } from "@visioni/a3s-codecs";

interface AlarmSettingsContentProps {
  cameraKey: string;
  siteId: string;
  showConfigSections?: boolean;
}

export function AlarmSettingsContent({ cameraKey, siteId, showConfigSections = true }: AlarmSettingsContentProps) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const tenantId = useTenantId();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { canManageAlarmSchedules } = useCameraPermissions(siteId, cameraKey);

  const { data: alarm, isLoading, isFetching, error, refetch } = useAlarm(siteId, cameraKey);

  const alarmTimezone = alarm?.settings?.timezone ?? alarm?.configured?.schedule?.metadata?.timezone ?? userTimezone;

  const historyRange = useMemo(() => {
    const nowInTz = toZonedTime(new Date(), alarmTimezone);
    const fromInTz = startOfWeek(addWeeks(nowInTz, -4), { weekStartsOn: 1 });
    return {
      from: toISODateString(fromZonedTime(fromInTz, alarmTimezone)),
      to: toISODateString(new Date()),
    };
  }, [alarmTimezone]);

  const { data: historyData, isLoading: isHistoryLoading } = useAlarmScheduleHistory({
    siteId: SiteId.fromString(siteId),
    assetCameraKey: AssetCameraKey.fromString(cameraKey),
    from: historyRange.from,
    to: historyRange.to,
  });

  const scheduleHistory: ScheduleHistoryEntry[] | undefined = useMemo(() => {
    if (!historyData?.data?.entries) return undefined;
    return historyData.data.entries.map((entry) => ({
      schedule: entry.schedule as unknown as AlarmSchedule,
      activeFrom: entry.activeFrom,
      activeUntil: entry.activeUntil,
    }));
  }, [historyData]);

  const schedule = useMemo(() => {
    return alarm?.configured?.schedule as AlarmSchedule | undefined;
  }, [alarm]);

  const exceptions = useMemo(() => {
    return alarm?.settings?.exceptions ?? [];
  }, [alarm]);
  const viewState = useMemo(() => {
    if (!alarm) return null;
    return deriveAlarmSettingsViewState({ alarmData: alarm, userTimezone });
  }, [alarm, userTimezone]);

  const content = (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: theme.spacing["6xl"],
        paddingBottom: theme.spacing["6xl"],
      }}
      showsVerticalScrollIndicator={false}
    >
      {/* Weekly Schedule Card */}
      <Card p="none" style={{ overflow: "hidden" }}>
        <View p="4xl">
          <WeeklySchedule
            key={`${cameraKey}-${alarm?.settings?.updatedAt ?? cameraKey}`}
            schedule={schedule}
            exceptions={exceptions}
            timezone={alarmTimezone}
            scheduleHistory={scheduleHistory}
            isHistoryLoading={isHistoryLoading}
          />
        </View>
      </Card>

      {showConfigSections && alarm && (
        <View mt="6xl">
          <AlarmConfigSections
            variant="mobile"
            pristine={viewState?.pristine ?? false}
            firstScheduleApplying={viewState?.firstScheduleApplying ?? false}
            schedulesDiffer={viewState?.schedulesDiffer ?? false}
            showTimezoneMismatchBanner={viewState?.showTimezoneMismatchBanner ?? false}
            timezone={viewState?.timezone ?? "UTC"}
            cameraTimezone={viewState?.cameraTimezone ?? "Not set"}
            userTimezone={userTimezone}
            routine={alarm.settings?.routine ?? null}
            exceptions={alarm.settings?.exceptions ?? []}
            response={alarm.settings?.response ?? null}
            onTimezonePress={() =>
              router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/timezone`)
            }
            onRoutinePress={() =>
              router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/routine`)
            }
            onAddExceptionPress={() =>
              router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exceptions/create`)
            }
            onExceptionPress={(exceptionId) =>
              router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exceptions/${exceptionId}`)
            }
            onResponsePress={() =>
              router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/response`)
            }
            onAppliedSchedulePress={() =>
              router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/applied-schedule`)
            }
            readOnly={!canManageAlarmSchedules}
          />
        </View>
      )}
    </ScrollView>
  );

  return (
    <DataStateView
      data={alarm}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      refetch={refetch}
      loadingMessage="Loading alarm settings..."
      errorTitle="Error Loading Alarm Settings"
      errorMessage="Failed to load alarm data for this camera"
      emptyState={{
        icon: "bellOffOutline",
        title: "No alarm settings",
        message: "No alarm settings configured for this camera.",
      }}
    >
      {() => content}
    </DataStateView>
  );
}
